import React, { useState } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Skeleton,
  Chip,
  Alert,
  Tooltip,
  IconButton,
  TextField,
  Button,
  Card,
  CardContent,
} from '@mui/material';
import {
  ShoppingCart as OrdersIcon,
  Inventory as ProductsIcon,
  People as VendorsIcon,
  Person as CustomersIcon,
  PersonAdd as NewCustomersIcon,
  AttachMoney as RevenueIcon,
  Cancel as CancelIcon,
  TrendingUp as ActiveIcon,
  ShoppingBasket as BasketIcon,
  AccountBalance as CommissionIcon,
  Store as StoreIcon,
  Refresh as RefreshIcon,
  Speed as PerformanceIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  DateRange as DateIcon,
  Campaign as CampaignIcon,
  Visibility as ImpressionsIcon,
  Mouse as ClicksIcon,
  MonetizationOn as CostIcon,
  Analytics as MetricsIcon,
  Facebook as FacebookIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  AccessTime as TimeIcon,
  DonutLarge as ChartIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { DashboardStats } from '../types/api';

interface StatsCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  subtitle?: string;
  loading?: boolean;
}

const StatsCard: React.FC<StatsCardProps> = React.memo(({ 
  title, 
  value, 
  icon, 
  color = 'primary',
  subtitle,
  loading = false 
}) => (
  <Paper 
    elevation={2} 
    sx={{ 
      p: 3, 
      height: '100%', 
      background: `linear-gradient(135deg, ${
        color === 'primary' ? '#1976d2' : 
        color === 'success' ? '#2e7d32' :
        color === 'warning' ? '#ed6c02' :
        color === 'error' ? '#d32f2f' :
        color === 'info' ? '#0288d1' :
        '#9c27b0'
      } 0%, ${
        color === 'primary' ? '#42a5f5' : 
        color === 'success' ? '#66bb6a' :
        color === 'warning' ? '#ff9800' :
        color === 'error' ? '#f44336' :
        color === 'info' ? '#29b6f6' :
        '#ba68c8'
      } 100%)`,
      color: 'white',
      position: 'relative',
      overflow: 'hidden'
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      <Box sx={{ mr: 2, opacity: 0.9 }}>
        {icon}
      </Box>
      <Typography variant="h6" component="div" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
    </Box>
    
    {loading ? (
      <Skeleton variant="text" width={120} height={48} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
    ) : (
      <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
    )}
    
    {subtitle && (
      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem' }}>
        {subtitle}
      </Typography>
    )}
    
    <Box 
      sx={{ 
        position: 'absolute', 
        top: -20, 
        right: -20, 
        opacity: 0.1, 
        transform: 'rotate(15deg)',
        fontSize: '4rem'
      }}
    >
      {icon}
    </Box>
  </Paper>
));

const Dashboard: React.FC = () => {
  const [dateFilter, setDateFilter] = useState({
    from: '',
    to: ''
  });

  // Separate state for pending filter changes (before applying)
  const [pendingDateFilter, setPendingDateFilter] = useState({
    from: '',
    to: ''
  });

  // Meta advertising period selection state
  const [selectedMetaPeriod, setSelectedMetaPeriod] = useState<'today' | 'last_7d' | 'last_30d' | 'custom'>('today');
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [metaData, setMetaData] = useState<any>(null);
  
  // Meta date filter state (separate from main dashboard date filter)
  const [metaDateFilter, setMetaDateFilter] = useState({
    from: '',
    to: ''
  });
  
  // Pending Meta date filter (before applying)
  const [pendingMetaDateFilter, setPendingMetaDateFilter] = useState({
    from: '',
    to: ''
  });

  // Optimized dashboard query with extended caching
  const { 
    data: dashboard, 
    isLoading, 
    error, 
    refetch,
    isFetching 
  }: UseQueryResult<DashboardStats, Error> = useQuery({
    queryKey: ['dashboard', dateFilter, selectedMetaPeriod, metaDateFilter],
    queryFn: () => api.getDashboard(
      dateFilter.from, 
      dateFilter.to, 
      false, 
      selectedMetaPeriod,
      selectedMetaPeriod === 'custom' ? metaDateFilter.from : undefined,
      selectedMetaPeriod === 'custom' ? metaDateFilter.to : undefined
    ),
    refetchInterval: 30 * 60 * 1000, // Extended: Refetch every 30 minutes (matches backend cache)
    staleTime: 25 * 60 * 1000, // Extended: Data is fresh for 25 minutes (slightly less than backend)
    retry: 2,
    retryDelay: 1000,
    // Add performance optimizations
    refetchOnWindowFocus: false, // Don't refetch on window focus (data is cached for 30 min)
    refetchOnReconnect: true, // Still refetch when reconnecting
  });

  // Force refresh function
  const handleForceRefresh = async () => {
    try {
      await api.getDashboard(
        dateFilter.from || undefined, 
        dateFilter.to || undefined,
        true, // Force refresh
        selectedMetaPeriod,
        selectedMetaPeriod === 'custom' ? metaDateFilter.from || undefined : undefined,
        selectedMetaPeriod === 'custom' ? metaDateFilter.to || undefined : undefined
      );
      refetch();
    } catch (error) {
      console.error('Force refresh failed:', error);
    }
  };

  // Clear cache function
  const handleClearCache = async () => {
    try {
      await api.clearCache();
      refetch();
    } catch (error) {
      console.error('Clear cache failed:', error);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'MAD') => {
    if (currency === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(amount);
    } else {
      return new Intl.NumberFormat('fr-MA', {
        style: 'currency',
        currency: 'MAD',
        minimumFractionDigits: 2,
      }).format(amount);
    }
  };

  // Helper function specifically for Meta advertising amounts (always USD)
  const formatMetaCurrency = (amount: number) => {
    return formatCurrency(amount, 'USD');
  };

  // Function to fetch Meta data for specific period
  const fetchMetaDataForPeriod = async (period: 'today' | 'last_7d' | 'last_30d') => {
    setMetaLoading(true);
    setMetaError(null);
    setSelectedMetaPeriod(period);
    
    try {
      // Refetch dashboard with new Meta period - this will get fresh data
      await refetch();
      setMetaData(null); // Clear separate meta data since we'll use dashboard data
    } catch (error) {
      console.error('Error fetching Meta data:', error);
      setMetaError(error instanceof Error ? error.message : 'Failed to fetch Meta data');
    } finally {
      setMetaLoading(false);
    }
  };

  // Helper functions for Meta data display
  const getCurrentMeta = () => {
    return metaData || dashboard?.meta_advertising || null;
  };

  const getPeriodData = (metaSource: any) => {
    if (!metaSource) return null;
    
    // Use the requested_data if available (this is the data for the selected period)
    if (metaSource.requested_data) {
      return metaSource.requested_data;
    }
    
    // Fallback to period-specific fields
    if (selectedMetaPeriod === 'today') return metaSource.today;
    if (selectedMetaPeriod === 'last_7d') return metaSource.last_7_days;
    if (selectedMetaPeriod === 'last_30d') return metaSource.last_30_days;
    
    // Final fallback to available data
    return metaSource.today || metaSource.last_7_days || metaSource.last_30_days || null;
  };

  const getDataPeriodLabel = () => {
    const currentMeta = getCurrentMeta();
    const periodData = getPeriodData(currentMeta);
    
    if (!periodData) return "No data available";
    
    // Use the backend's requested_period if available
    if (currentMeta?.requested_period) {
      if (currentMeta.requested_period === 'today') return "Today";
      if (currentMeta.requested_period === 'last_7d') return "Last 7 days";  
      if (currentMeta.requested_period === 'last_30d') return "Last 30 days";
    }
    
    // Fallback to frontend selection
    if (selectedMetaPeriod === 'today') return "Today";
    if (selectedMetaPeriod === 'last_7d') return "Last 7 days";
    if (selectedMetaPeriod === 'last_30d') return "Last 30 days";
    
    return "Data available";
  };



  const handleApplyFilter = () => {
    setDateFilter({
      from: pendingDateFilter.from,
      to: pendingDateFilter.to
    });
  };

  const handleClearFilter = () => {
    setPendingDateFilter({ from: '', to: '' });
    setDateFilter({ from: '', to: '' });
  };

  const hasActiveFilter = dateFilter.from || dateFilter.to;
  const hasChanges = pendingDateFilter.from !== dateFilter.from || pendingDateFilter.to !== dateFilter.to;

  // Meta date filter handlers
  const handleApplyMetaFilter = () => {
    setMetaDateFilter(pendingMetaDateFilter);
    setSelectedMetaPeriod('custom');
  };

  const handleClearMetaFilter = () => {
    setMetaDateFilter({ from: '', to: '' });
    setPendingMetaDateFilter({ from: '', to: '' });
    setSelectedMetaPeriod('today');
  };

  // Check if there are changes in Meta date filter
  const hasMetaChanges = pendingMetaDateFilter.from !== metaDateFilter.from || 
                        pendingMetaDateFilter.to !== metaDateFilter.to;
  const hasActiveMetaFilter = metaDateFilter.from || metaDateFilter.to;

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading dashboard: {error.message}
          <IconButton size="small" onClick={() => refetch()} sx={{ ml: 2 }}>
            <RefreshIcon />
          </IconButton>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Modern Header Section */}
      <Box sx={{ 
        mb: 4, 
        p: 3, 
        background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(124, 58, 237, 0.08) 100%)',
        borderRadius: 3,
        border: '1px solid rgba(37, 99, 235, 0.1)',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography 
              variant="h3" 
              component="h1" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #ed762e 0%, #f59045 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              <DashboardIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Dashboard Overview
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
              Real-time business insights and analytics
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {dashboard?.cached && (
              <Tooltip title={`Extended 30-minute cache enabled. Cache expires: ${new Date(new Date(dashboard.cache_time!).getTime() + 30 * 60 * 1000).toLocaleTimeString()}`}>
                <Chip 
                  icon={<PerformanceIcon />} 
                  label={`Extended Cache (${dashboard.cache_duration_minutes || 30}min)`}
                  size="small" 
                  color="success" 
                  variant="outlined"
                />
              </Tooltip>
            )}
            {dashboard?.smart_meta_caching_enabled && dashboard?.meta_cache_info && (
              <Tooltip title={`Meta data cached: ${dashboard.meta_cache_info.cache_type} data cached for ${dashboard.meta_cache_info.cache_expiry_hours}h`}>
                <Chip 
                  icon={<FacebookIcon />} 
                  label={`Smart Meta Cache (${dashboard.meta_cache_info.cache_type})`}
                  size="small" 
                  color={dashboard.meta_cache_info.cache_type === 'historical' ? 'info' : 'warning'} 
                  variant="outlined"
                />
              </Tooltip>
            )}
            {dashboard?.force_refresh && (
              <Chip 
                label="Fresh Data"
                size="small" 
                color="success" 
                variant="outlined"
              />
            )}
            <Tooltip title="Refresh Dashboard (Cache)">
              <IconButton 
                onClick={() => refetch()} 
                disabled={isFetching}
                sx={{ 
                  background: 'linear-gradient(135deg, #ed762e 0%, #f59045 100%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d6621a 0%, #ed762e 100%)',
                  },
                  '&:disabled': {
                    background: 'rgba(0,0,0,0.12)',
                  }
                }}
              >
                {isFetching ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Force Fresh Data">
              <IconButton 
                onClick={handleForceRefresh} 
                disabled={isFetching}
                sx={{ 
                  background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  },
                  '&:disabled': {
                    background: 'rgba(0,0,0,0.12)',
                  }
                }}
              >
                {isFetching ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Clear All Cache">
              <IconButton 
                onClick={handleClearCache} 
                disabled={isFetching}
                sx={{ 
                  background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                  },
                  '&:disabled': {
                    background: 'rgba(0,0,0,0.12)',
                  }
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Smart Date Filter Section with Presets */}
        <Card sx={{ mt: 3, borderRadius: 2, border: '1px solid rgba(0,0,0,0.06)' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <DateIcon sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Date Range Filter
              </Typography>
            </Box>

            {/* Quick Preset Buttons */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
              <Button
                variant={!hasActiveFilter ? 'contained' : 'outlined'}
                size="small"
                onClick={handleClearFilter}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                All Time
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const yesterday = new Date();
                  yesterday.setDate(yesterday.getDate() - 1);
                  const dateStr = yesterday.toISOString().split('T')[0];
                  setPendingDateFilter({ from: dateStr, to: dateStr });
                  setDateFilter({ from: dateStr, to: dateStr });
                }}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Yesterday
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const sevenDaysAgo = new Date();
                  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                  const today = new Date();
                  const fromDate = sevenDaysAgo.toISOString().split('T')[0];
                  const toDate = today.toISOString().split('T')[0];
                  setPendingDateFilter({ from: fromDate, to: toDate });
                  setDateFilter({ from: fromDate, to: toDate });
                }}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Last 7 Days
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const thirtyDaysAgo = new Date();
                  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                  const today = new Date();
                  const fromDate = thirtyDaysAgo.toISOString().split('T')[0];
                  const toDate = today.toISOString().split('T')[0];
                  setPendingDateFilter({ from: fromDate, to: toDate });
                  setDateFilter({ from: fromDate, to: toDate });
                }}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Last 30 Days
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const sixtyDaysAgo = new Date();
                  sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
                  const today = new Date();
                  const fromDate = sixtyDaysAgo.toISOString().split('T')[0];
                  const toDate = today.toISOString().split('T')[0];
                  setPendingDateFilter({ from: fromDate, to: toDate });
                  setDateFilter({ from: fromDate, to: toDate });
                }}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Last 60 Days
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const ninetyDaysAgo = new Date();
                  ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
                  const today = new Date();
                  const fromDate = ninetyDaysAgo.toISOString().split('T')[0];
                  const toDate = today.toISOString().split('T')[0];
                  setPendingDateFilter({ from: fromDate, to: toDate });
                  setDateFilter({ from: fromDate, to: toDate });
                }}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Last 90 Days
              </Button>
            </Box>

            {/* Custom Date Inputs */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Custom Range:
              </Typography>
              
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={pendingDateFilter.from}
                onChange={(e) => setPendingDateFilter(prev => ({ ...prev, from: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ 
                  minWidth: 150,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              
              <TextField
                label="To Date"
                type="date"
                size="small"
                value={pendingDateFilter.to}
                onChange={(e) => setPendingDateFilter(prev => ({ ...prev, to: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ 
                  minWidth: 150,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              
              <Button
                variant="contained"
                size="small"
                startIcon={<FilterIcon />}
                onClick={handleApplyFilter}
                disabled={!hasChanges}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  background: 'linear-gradient(135deg, #ed762e 0%, #f59045 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d6621a 0%, #ed762e 100%)',
                  }
                }}
              >
                Apply Custom
              </Button>
              
              {hasActiveFilter && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilter}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  Clear All
                </Button>
              )}
            </Box>
            
            {hasActiveFilter && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                  📅 Active Filter: {dateFilter.from || 'All time'} {dateFilter.to && dateFilter.from !== dateFilter.to ? `to ${dateFilter.to}` : ''}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Stats Cards Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Customers"
            value={dashboard?.total_customers || 0}
            icon={<CustomersIcon />}
            color="primary"
            subtitle="Who completed purchases"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="New This Week"
            value={dashboard?.new_customers_this_week || 0}
            icon={<NewCustomersIcon />}
            color="success"
            subtitle="Last 7 days"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Average Basket"
            value={dashboard ? formatCurrency(dashboard.average_basket_size) : formatCurrency(0)}
            icon={<BasketIcon />}
            color="info"
            subtitle="Per order"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Cancellation Rate"
            value={dashboard ? `${dashboard.order_cancellation_rate}%` : '0%'}
            icon={<CancelIcon />}
            color="warning"
            subtitle="Failed/cancelled orders"
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Business Metrics */}
      <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
        <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Business Metrics
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Orders"
            value={dashboard?.total_orders || 0}
            icon={<OrdersIcon />}
            color="primary"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Products"
            value={dashboard?.total_products || 0}
            icon={<ProductsIcon />}
            color="secondary"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Active Merchants"
            value={dashboard?.active_merchants || 0}
            icon={<ActiveIcon />}
            color="success"
            subtitle="With recent orders"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Vendors"
            value={dashboard?.total_vendors || 0}
            icon={<VendorsIcon />}
            color="info"
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Revenue & Commission */}
      <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
        <RevenueIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Revenue & Commission
      </Typography>
      
      {/* Completed Orders Section */}
      <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', fontWeight: 500 }}>
        📋 Completed Orders Only
      </Typography>
      

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <StatsCard
            title="Vendor Earnings"
            value={dashboard ? formatCurrency(dashboard.completed_vendor_earnings || 0) : formatCurrency(0)}
            icon={<StoreIcon />}
            color="success"
            subtitle="After shipping & 5% commission deducted"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StatsCard
            title="OROUD Commission"
            value={dashboard ? formatCurrency(dashboard.completed_oroud_commission || 0) : formatCurrency(0)}
            icon={<CommissionIcon />}
            color="warning"
            subtitle="5% of items subtotal (no shipping)"
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Completed + Processing Orders Section */}
      <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', fontWeight: 500 }}>
        🔄 Completed + Processing Orders
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <StatsCard
            title="Vendor Earnings"
            value={dashboard ? formatCurrency(dashboard.all_vendor_earnings || 0) : formatCurrency(0)}
            icon={<BusinessIcon />}
            color="info"
            subtitle="After shipping & 5% commission deducted"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StatsCard
            title="OROUD Commission"
            value={dashboard ? formatCurrency(dashboard.all_oroud_commission || 0) : formatCurrency(0)}
            icon={<RevenueIcon />}
            color="secondary"
            subtitle="5% of items subtotal (no shipping)"
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Meta Advertising Section */}
      <Typography variant="h5" sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
        <FacebookIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Meta Advertising Campaigns
      </Typography>
      
      {/* Meta Data Period Selector */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
            <TimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Time Period
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant={selectedMetaPeriod === 'today' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => fetchMetaDataForPeriod('today')}
              disabled={metaLoading}
              startIcon={metaLoading && selectedMetaPeriod === 'today' ? <CircularProgress size={16} /> : null}
            >
              Today
            </Button>
            <Button
              variant={selectedMetaPeriod === 'last_7d' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => fetchMetaDataForPeriod('last_7d')}
              disabled={metaLoading}
              startIcon={metaLoading && selectedMetaPeriod === 'last_7d' ? <CircularProgress size={16} /> : null}
            >
              Last 7 Days
            </Button>
            <Button
              variant={selectedMetaPeriod === 'last_30d' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => fetchMetaDataForPeriod('last_30d')}
              disabled={metaLoading}
              startIcon={metaLoading && selectedMetaPeriod === 'last_30d' ? <CircularProgress size={16} /> : null}
            >
              Last 30 Days
            </Button>
            <Button
              variant={selectedMetaPeriod === 'custom' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setSelectedMetaPeriod('custom')}
              disabled={metaLoading}
            >
              Custom Range
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleForceRefresh()}
              disabled={metaLoading || isFetching}
              startIcon={isFetching || metaLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
            >
              Refresh All
            </Button>
          </Box>
        </Box>
        
        {/* Custom Date Range Filter for Meta */}
        {selectedMetaPeriod === 'custom' && (
          <Box sx={{ mt: 3, p: 2, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
              Custom Meta Date Range:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={pendingMetaDateFilter.from}
                onChange={(e) => setPendingMetaDateFilter(prev => ({ ...prev, from: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              
              <TextField
                label="To Date"
                type="date"
                size="small"
                value={pendingMetaDateFilter.to}
                onChange={(e) => setPendingMetaDateFilter(prev => ({ ...prev, to: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              
              <Button
                variant="contained"
                size="small"
                startIcon={<FilterIcon />}
                onClick={handleApplyMetaFilter}
                disabled={!hasMetaChanges || !pendingMetaDateFilter.from || !pendingMetaDateFilter.to}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Apply Meta Filter
              </Button>
              
              {hasActiveMetaFilter && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearMetaFilter}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  Clear Meta Filter
                </Button>
              )}
            </Box>
            
            {hasActiveMetaFilter && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Active Meta filter: {metaDateFilter.from} to {metaDateFilter.to}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        
        {metaError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Error fetching Meta data: {metaError}
          </Alert>
        )}
        {/* Meta data loaded successfully */}
      </Paper>
      
      {/* Meta Data Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box />
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={handleForceRefresh}
          disabled={isFetching}
          sx={{ textTransform: 'none' }}
        >
          Refresh Meta Data
        </Button>
      </Box>
      
      {(dashboard?.meta_advertising?.success && dashboard?.meta_advertising?.account_info) ? (
        <>
          {/* Meta KPIs - Display data from selected period or metaData */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard
                title="Ad Spend"
                value={(() => {
                  const currentMeta = getCurrentMeta();
                  const periodData = getPeriodData(currentMeta);
                  return periodData ? formatMetaCurrency(periodData.total_spend) : formatMetaCurrency(0);
                })()}
                icon={<CostIcon />}
                color="error"
                subtitle={getDataPeriodLabel()}
                loading={isLoading || metaLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard
                title="Cost per Purchase"
                value={(() => {
                  const currentMeta = getCurrentMeta();
                  const periodData = getPeriodData(currentMeta);
                  return periodData ? formatMetaCurrency(periodData.cost_per_purchase || 0) : formatMetaCurrency(0);
                })()}
                icon={<CostIcon />}
                color="warning"
                subtitle={getDataPeriodLabel()}
                loading={isLoading || metaLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard
                title="ROAS"
                value={(() => {
                  const currentMeta = getCurrentMeta();
                  const periodData = getPeriodData(currentMeta);
                  return periodData ? `${periodData.return_on_ad_spend || 0}x` : '0x';
                })()}
                icon={<ActiveIcon />}
                color="success"
                subtitle={getDataPeriodLabel()}
                loading={isLoading || metaLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard
                title="Purchases"
                value={(() => {
                  const currentMeta = getCurrentMeta();
                  const periodData = getPeriodData(currentMeta);
                  return periodData ? (periodData.total_purchases || 0) : 0;
                })()}
                icon={<OrdersIcon />}
                color="info"
                subtitle={getDataPeriodLabel()}
                loading={isLoading || metaLoading}
              />
            </Grid>
          </Grid>

          {/* Meta Performance Metrics */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard
                title="Impressions"
                value={(() => {
                  const currentMeta = getCurrentMeta();
                  const periodData = getPeriodData(currentMeta);
                  return periodData ? (periodData.total_impressions || 0).toLocaleString() : '0';
                })()}
                icon={<ImpressionsIcon />}
                color="primary"
                subtitle={getDataPeriodLabel()}
                loading={isLoading || metaLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard
                title="Clicks"
                value={(() => {
                  const currentMeta = getCurrentMeta();
                  const periodData = getPeriodData(currentMeta);
                  return periodData ? (periodData.total_clicks || 0).toLocaleString() : '0';
                })()}
                icon={<ClicksIcon />}
                color="secondary"
                subtitle={getDataPeriodLabel()}
                loading={isLoading || metaLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard
                title="CTR"
                value={(() => {
                  const currentMeta = getCurrentMeta();
                  const periodData = getPeriodData(currentMeta);
                  return periodData ? `${periodData.click_through_rate || 0}%` : '0%';
                })()}
                icon={<MetricsIcon />}
                color="info"
                subtitle={getDataPeriodLabel()}
                loading={isLoading || metaLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard
                title="CPM"
                value={(() => {
                  const currentMeta = getCurrentMeta();
                  const periodData = getPeriodData(currentMeta);
                  return periodData ? formatMetaCurrency(periodData.cost_per_mille || 0) : formatMetaCurrency(0);
                })()}
                icon={<CostIcon />}
                color="warning"
                subtitle={getDataPeriodLabel()}
                loading={isLoading || metaLoading}
              />
            </Grid>
          </Grid>

          {/* Top Campaigns */}
          {(() => {
            const currentMeta = getCurrentMeta();
            return currentMeta?.top_campaigns && currentMeta.top_campaigns.length > 0 && (
              <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CampaignIcon /> Top Performing Campaigns
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Campaign Name</TableCell>
                        <TableCell align="right">Spend</TableCell>
                        <TableCell align="right">Purchases</TableCell>
                        <TableCell align="right">ROAS</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentMeta.top_campaigns.map((campaign: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell sx={{ maxWidth: 200 }}>
                            <Typography variant="body2" noWrap title={campaign.name}>
                              {campaign.name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="error">
                              {formatMetaCurrency(campaign.spend)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="primary">
                              {campaign.purchases}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="success.main">
                              {campaign.roas}x
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            );
          })()}

          {/* Meta Account Info */}
          {(() => {
            const currentMeta = getCurrentMeta();
            return currentMeta?.account_info && (
              <Alert severity="info" sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <FacebookIcon />
                  <Typography variant="body2">
                    <strong>Account:</strong> {currentMeta.account_info.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Currency:</strong> {currentMeta.account_info.currency}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Spent:</strong> {formatMetaCurrency(parseFloat(currentMeta.account_info.amount_spent || '0'))}
                  </Typography>
                  {currentMeta.cached && (
                    <Chip 
                      label={`Cached: ${currentMeta.cache_time}`}
                      size="small" 
                      variant="outlined"
                    />
                  )}
                </Box>
              </Alert>
            );
          })()}
        </>
      ) : (
        <Alert severity="warning" sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FacebookIcon />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Meta Advertising Data Not Available
              </Typography>
              <Typography variant="body2">
                {dashboard?.meta_advertising?.error 
                  ? `Error: ${dashboard.meta_advertising.error}` 
                  : "Please configure your Meta Ad Account ID in the backend settings to view advertising metrics."
                }
              </Typography>
              {dashboard?.meta_advertising && !dashboard?.meta_advertising?.success && (
                <Typography variant="body2" sx={{ mt: 1, color: 'error.main' }}>
                  <strong>Debug Info:</strong> Meta API call failed or returned invalid data.
                </Typography>
              )}
            </Box>
          </Box>
        </Alert>
      )}

      {/* Order Status Distribution & Best Selling Products Side by Side */}
      <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
        <ChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Analytics Overview
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Order Status Distribution */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <OrdersIcon /> Order Status Breakdown
            </Typography>
            {isLoading ? (
              <Box>
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} variant="text" height={40} sx={{ mb: 1 }} />
                ))}
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(dashboard?.order_status_counts || {}).map(([status, count]) => (
                      <TableRow key={status}>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{status}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={count} 
                            size="small" 
                            color={
                              status === 'completed' ? 'success' :
                              status === 'processing' ? 'warning' :
                              status === 'cancelled' ? 'error' : 'default'
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Best Selling Products - Compact Version */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarIcon /> Top Products
            </Typography>
            {isLoading ? (
              <Box>
                {[...Array(3)].map((_, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Skeleton variant="rectangular" width={40} height={40} sx={{ mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" height={24} width="80%" />
                      <Skeleton variant="text" height={18} width="50%" />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {dashboard?.best_selling_products?.slice(0, 5).map((product, index) => (
                  <Box key={product.id} sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 2, 
                    p: 1, 
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'grey.50' }
                  }}>
                    {product.image && (
                      <Box
                        component="img"
                        src={product.image}
                        alt={product.name}
                        sx={{ width: 40, height: 40, mr: 2, borderRadius: 1 }}
                      />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                        {product.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip 
                          label={formatCurrency(parseFloat(product.price || '0'))} 
                          size="small" 
                          variant="outlined"
                        />
                        <Chip 
                          label={`${product.total_sales} sales`} 
                          size="small" 
                          color="success" 
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;